// Axios configuration and instance
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, API_TIMEOUT, LOCAL_STORAGE_KEYS } from '@constants';
import { ApiError } from '@types';
import { storage } from '@utils';

// Create axios instance
const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: true, // Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token to requests
    const token = storage.local.get<string>(LOCAL_STORAGE_KEYS.TOKEN);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Return the response data directly
    return response.data;
  },
  (error: AxiosError) => {
    // Handle errors
    const apiError: ApiError = {
      message: 'An error occurred',
      statusCode: error.response?.status || 500,
    };

    if (error.response) {
      // Server responded with error
      const data = error.response.data as { message?: string; error?: string };
      apiError.message = data?.message || error.message;
      apiError.error = data?.error;
      apiError.details = error.response.data as Record<string, unknown>;
    } else if (error.request) {
      // Request made but no response
      apiError.message = 'No response from server';
    } else {
      // Something else happened
      apiError.message = error.message;
    }

    // Handle 401 Unauthorized
    // Only force legacy logout when we actually sent a bearer token.
    // Ory cookie-based sessions can hit permission-scoped 401/403 responses
    // for optional endpoints (e.g. admin-only lookups) and should not be
    // treated as a full sign-out event.
    const authHeader = error.config?.headers?.Authorization;
    const usedBearerToken = typeof authHeader === 'string' && authHeader.startsWith('Bearer ');

    if (error.response?.status === 401 && usedBearerToken) {
      storage.local.clear();
      window.location.href = '/login';
    }

    return Promise.reject(apiError);
  }
);

export default axiosInstance;
